import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { ChevronDown, ChevronUp, FileCode, ExternalLink } from 'lucide-react'
import type { FindingResponse, CodeSnippet, FeedbackVerdict } from '../../types/api'
import { SUBCATEGORY_LABELS } from '../../types/api'
import { CategoryBadge, SeverityBadge, TonePill } from '../shared/Badge'
import { scansApi } from '../../api/scans'

// ---------------------------------------------------------------------------
// Severity visual tokens
// ---------------------------------------------------------------------------

// The severity fill moved from rest to hover (DEC-136).
//
// A permanent tint was 6% of the hue over `ground` on dark and 4% over paper on
// light, and in neither theme did the four read as four severities: on dark they
// land within a few RGB points of each other, on light they are four
// indistinguishable beiges. It could not simply be deepened, because `faint`
// sits on it and 4.5:1 against `faint` is the cap. So the resting fill was
// spending the whole contrast budget to say almost nothing, and leaving none
// for hover, which is why DEC-133 had to make hover a bar rather than a
// surface change. That finding is not reversed here; the card just is not
// tinted at rest any more, so the budget is unspent when hover arrives.
//
// Deliberately `wash-*` and not `tint-*`. (CodeView now has its own `--code-*` tokens; it once read
// `tint-*`, so folding the two together would have restyled the code snippet
// as a side effect of a card change.
const SEVERITY_WASH: Record<string, string> = {
  critical: 'hover:bg-wash-critical',
  high:     'hover:bg-wash-high',
  medium:   'hover:bg-wash-medium',
  low:      'hover:bg-wash-low',
}

// At rest this bar is the only thing carrying severity besides the label, so it
// is a 3x15 mark rather than the old 8px dot (DEC-136). It sits INSIDE the card
// padding, never on the card edge: an edge rail was the rejected alternative,
// and it would have required reopening DEC-127.5's rule that no card carries a
// coloured left border.
// The mark hues, the same as the pill dots (DEC-193). Every one clears 3:1 on
// the card, so DEC-183's ring on High is gone.
const SEVERITY_MARK: Record<string, string> = {
  critical: 'bg-mark-critical',
  high:     'bg-mark-high',
  medium:   'bg-mark-medium',
  low:      'bg-mark-low',
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface FindingCardProps {
  finding: FindingResponse
  hourlyRate?: number
  repoUrl?: string
  branch?: string
  /** Suppress the file path in collapsed header (used in by-file grouping mode). */
  hideFile?: boolean
  /** Scan ID — required for feedback API calls. */
  scanId?: string
  /** Called when the user submits or removes a verdict. */
  onVerdictChange?: (findingId: string, verdict: FeedbackVerdict | null) => void
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function dollarCost(finding: FindingResponse, hourlyRate = 150): number {
  const multipliers: Record<string, number> = {
    low: 0.5, medium: 1.0, high: 2.0, critical: 4.0,
  }
  return Math.round(
    finding.estimated_effort_hours * hourlyRate * (multipliers[finding.severity] ?? 1.0),
  )
}

function buildGithubUrl(
  repoUrl: string,
  branch: string,
  file: string,
  lineStart: number | null,
  lineEnd: number | null,
): string {
  const base = repoUrl.replace(/\.git$/, '')
  let url = `${base}/blob/${branch}/${file}`
  if (lineStart !== null) {
    url += `#L${lineStart}`
    if (lineEnd !== null && lineEnd !== lineStart) url += `-L${lineEnd}`
  }
  return url
}

function isGithubRepo(repoUrl?: string): boolean {
  return !!repoUrl && repoUrl.startsWith('https://github.com/')
}

function subcategoryLabel(subcategory: string): string {
  return SUBCATEGORY_LABELS[subcategory] ?? subcategory.replace(/_/g, ' ')
}

// ---------------------------------------------------------------------------
// FileChip
// ---------------------------------------------------------------------------

interface FileChipProps {
  file: string
  lineStart: number | null
  lineEnd: number | null
  repoUrl?: string
  branch?: string
  lineOnly?: boolean
}

function FileChip({ file, lineStart, lineEnd, repoUrl, branch, lineOnly }: FileChipProps) {
  const linked = isGithubRepo(repoUrl) && !!branch
  const lineLabel =
    lineStart !== null
      ? lineEnd !== null && lineEnd !== lineStart
        ? `:${lineStart}–${lineEnd}`
        : `:${lineStart}`
      : ''

  const label = lineOnly ? (lineLabel || file) : `${file}${lineLabel}`

  const inner = (
    <span className="inline-flex items-center gap-1 text-[13px] tabular-nums text-body hover:text-ink">
      {!lineOnly && <FileCode className="w-3.5 h-3.5 text-faint shrink-0" />}
      <span>{label}</span>
      {linked && !lineOnly && <ExternalLink className="w-3 h-3 text-faint shrink-0" />}
    </span>
  )

  if (linked && repoUrl && branch) {
    const href = buildGithubUrl(repoUrl, branch, file, lineStart, lineEnd)
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
        className="inline-flex"
        title={`Open in GitHub: ${file}${lineLabel}`}
      >
        {inner}
      </a>
    )
  }
  return <span className="inline-flex">{inner}</span>
}

// ---------------------------------------------------------------------------
// CodeView
// ---------------------------------------------------------------------------

interface CodeViewProps {
  snippet: CodeSnippet
  highlightStart: number | null
  highlightEnd: number | null
  ext: string
  severity: string
}

// Highlight follows the finding's severity family (DEC-068). Code has its own surface and
// tokens (`--code-*` in index.css), so page palette changes never restyle a snippet.
const SEVERITY_KEYS = ['critical', 'high', 'medium', 'low'] as const

function CodeView({ snippet, highlightStart, highlightEnd, ext, severity }: CodeViewProps) {
  const lines = snippet.text.split('\n')
  const startLine = snippet.snippet_start_line
  const sev = (SEVERITY_KEYS as readonly string[]).includes(severity) ? severity : 'medium'

  return (
    <div className="mt-1">
      <div className="flex items-center justify-between mb-1">
        <p className="text-[11px] font-semibold text-muted uppercase tracking-wider">Code</p>
        {ext && (
          <span className="text-[11px] text-faint bg-fill-subtle px-1.5 py-0.5 rounded">
            {ext}
          </span>
        )}
      </div>
      <div
        className="rounded-md overflow-x-auto font-mono text-[12.5px] leading-[1.65]"
        style={{ background: 'var(--code-bg)', border: '1px solid var(--code-line)' }}
        data-testid="code-view"
      >
        <table className="w-full border-collapse">
          <tbody>
            {lines.map((line, i) => {
              const lineNum = startLine + i
              const isHighlighted =
                highlightStart !== null &&
                highlightEnd !== null &&
                lineNum >= highlightStart &&
                lineNum <= highlightEnd
              return (
                <tr
                  key={i}
                  data-highlighted={isHighlighted || undefined}
                  style={isHighlighted ? { background: `var(--code-hl-${sev})` } : undefined}
                >
                  <td
                    className="select-none text-right align-top tabular-nums w-[52px] pl-3 pr-3"
                    style={{
                      background: isHighlighted ? 'transparent' : 'var(--code-gutter)',
                      color: isHighlighted ? 'var(--code-ink)' : 'var(--code-num)',
                      borderRight: '1px solid var(--code-line)',
                      boxShadow: isHighlighted ? `inset 3px 0 0 var(--code-mark-${sev})` : undefined,
                      fontWeight: isHighlighted ? 600 : 400,
                    }}
                  >
                    {lineNum}
                  </td>
                  <td className="pl-4 pr-4 whitespace-pre" style={{ color: 'var(--code-ink)' }}>
                    {line || ' '}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// FindingCard
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// FeedbackRow — verdict buttons + prior-signal badge
// ---------------------------------------------------------------------------

interface FeedbackRowProps {
  scanId: string
  finding: FindingResponse
  localVerdict: FeedbackVerdict | null
  onVerdictChange: (findingId: string, verdict: FeedbackVerdict | null) => void
}

function FeedbackRow({ scanId, finding, localVerdict, onVerdictChange }: FeedbackRowProps) {
  const [error, setError] = useState<string | null>(null)

  const submitMutation = useMutation({
    mutationFn: (verdict: FeedbackVerdict) =>
      scansApi.submitFeedback(scanId, finding.finding_id, { verdict }),
    onSuccess: (_data, verdict) => {
      setError(null)
      onVerdictChange(finding.finding_id, verdict)
    },
    onError: () => setError('Could not save feedback. Try again.'),
  })

  const deleteMutation = useMutation({
    mutationFn: () => scansApi.deleteFeedback(scanId, finding.finding_id),
    onSuccess: () => {
      setError(null)
      onVerdictChange(finding.finding_id, null)
    },
    onError: () => setError('Could not remove feedback. Try again.'),
  })

  const isPending = submitMutation.isPending || deleteMutation.isPending

  function handleClick(verdict: FeedbackVerdict) {
    if (isPending) return
    if (localVerdict === verdict) {
      deleteMutation.mutate()
    } else {
      submitMutation.mutate(verdict)
    }
  }

  const VERDICTS: { key: FeedbackVerdict; label: string; active: string; idle: string }[] = [
    {
      key: 'confirmed',
      label: '✓ Confirmed',
      active: 'bg-success text-white border-success',
      idle:   'border-hairline text-muted hover:border-success/50 hover:text-success',
    },
    {
      key: 'false_positive',
      label: '✕ False Positive',
      active: 'bg-critical text-white border-critical',
      idle:   'border-hairline text-muted hover:border-critical/50 hover:text-critical',
    },
    {
      key: 'wont_fix',
      label: '→ Won\'t Fix',
      active: 'bg-warn text-ground border-warn',
      idle:   'border-hairline text-muted hover:border-warn/50 hover:text-warn',
    },
  ]

  return (
    <div className="pt-3 border-t border-hairline">
      <p className="text-[11px] text-faint mb-2">Was this finding correct?</p>
      <div className="flex flex-wrap gap-2">
        {VERDICTS.map(({ key, label, active, idle }) => {
          const isActive = localVerdict === key
          const isLoading = isPending && submitMutation.variables === key
          return (
            <button
              key={key}
              onClick={() => handleClick(key)}
              disabled={isPending}
              className={`text-[12px] font-medium px-3 py-1 rounded-full border transition-colors ${
                isActive ? active : idle
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {isLoading ? '…' : label}
            </button>
          )
        })}
      </div>
      {error && (
        <p className="text-[11px] text-critical mt-1">{error}</p>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// DEC-067: LLM-backed detection methods get the AI-validated chip; everything
// else (including null from pre-013 scans) reads as static.
// DEC-145: provenance is two facts, not one. `confirmed_by` is set when an LLM step
// confirmed or enriched a finding; `detection_method` always names the detector.
// This used to sniff substrings out of detection_method, which the validators had
// already overwritten with "llm_tool_use" — so a Bandit hit the model merely agreed
// with displayed identically to something the model asserted on its own. It also read
// D10's LLM findings as "static", because D10 never set the field at all.
const AI_DETECTION_METHODS = ['llm_pass1', 'llm_pass2', 'llm_tool_use']

function isAiValidated(finding: FindingResponse): boolean {
  if (finding.confirmed_by) return true
  // D11 discovery and D10 both produce findings the model FOUND, not confirmed.
  return AI_DETECTION_METHODS.includes((finding.detection_method ?? '').toLowerCase())
}

/** What the hover text says. Provenance stays inspectable either way (AI Explicability). */
function provenanceTitle(finding: FindingResponse): string {
  const detector = finding.detection_method ?? 'static analysis'
  return finding.confirmed_by
    ? `found by ${detector}, confirmed by ${finding.confirmed_by}`
    : `found by ${detector}`
}

// ConfidenceAdjBadge — shown next to the confidence % when adjusted
// ---------------------------------------------------------------------------

interface ConfidenceAdjBadgeProps {
  adjustment: number
  reason: string
}

function ConfidenceAdjBadge({ adjustment, reason }: ConfidenceAdjBadgeProps) {
  const [showTip, setShowTip] = useState(false)
  const isFp = adjustment < 0
  const color = isFp ? 'text-critical' : 'text-success'
  const label = isFp
    ? `↓FP`
    : `↑Conf`

  return (
    <span className="relative inline-flex items-center">
      <button
        className={`text-[11px] font-semibold ${color} ml-1 cursor-help`}
        onClick={(e) => { e.stopPropagation(); setShowTip((v) => !v) }}
        title={reason}
        aria-label="Confidence adjusted, click for details"
      >
        {label} {adjustment > 0 ? '+' : ''}{(adjustment * 100).toFixed(0)}%
      </button>
      {showTip && (
        <div
          className="absolute bottom-full left-0 mb-1 z-10 w-72 bg-raised text-ink text-[11px] leading-snug rounded-lg px-3 py-2 shadow-overlay"
          onClick={(e) => e.stopPropagation()}
        >
          {reason}
          <button
            className="ml-2 text-faint hover:text-ink"
            onClick={() => setShowTip(false)}
          >
            ×
          </button>
        </div>
      )}
    </span>
  )
}

// ---------------------------------------------------------------------------
// FindingCard
// ---------------------------------------------------------------------------

export function FindingCard({
  finding,
  hourlyRate = 150,
  repoUrl,
  branch,
  hideFile = false,
  scanId,
  onVerdictChange,
}: FindingCardProps) {
  const [expanded, setExpanded] = useState(false)
  // Local verdict starts from the API value; updated optimistically on user action
  const [localVerdict, setLocalVerdict] = useState<FeedbackVerdict | null>(
    finding.feedback_verdict ?? null,
  )

  function handleVerdictChange(findingId: string, verdict: FeedbackVerdict | null) {
    setLocalVerdict(verdict)
    onVerdictChange?.(findingId, verdict)
  }
  const cost = dollarCost(finding, hourlyRate)
  const isGrouped = finding.occurrence_count > 1
  const fileExt = finding.file.includes('.') ? finding.file.split('.').pop() ?? '' : ''

  const washClass = SEVERITY_WASH[finding.severity] ?? ''
  const markClass = SEVERITY_MARK[finding.severity] ?? 'bg-hairline-strong'
  const knownSeverity = (['critical', 'high', 'medium', 'low'] as const).find((k) => k === finding.severity)
  const subLabel = subcategoryLabel(finding.subcategory)

  return (
    <div
      // `data-hue` lives here rather than on the button so both the border
      // below and the button inside resolve the same `--hue`.
      //
      // The border is scoped to `:has(> button:hover)` and not plain `hover:`
      // on purpose: the expanded detail is the div's other direct child and
      // contains its own buttons, so a bare hover would light the border while
      // someone reached for a feedback pill. Restricting it to the direct-child
      // header keeps hover meaning "the row", which is where it lives today.
      data-hue={finding.severity}
      className="border border-hairline bg-panel rounded-lg shadow-card overflow-hidden transition-colors [&:has(>button:hover)]:border-hue/40"
    >
      {/* ── Collapsed header ── */}
      <button
        // The fill lands on the header row only, never the card, so an open
        // card's detail body is not washed while its header is hovered
        // (DEC-136). Replaces DEC-133's `shadow-hue-bar`, which existed
        // because a tinted card had no headroom for a wash. This one does.
        className={`w-full text-left px-4 py-3 flex items-start gap-3 transition-colors ${washClass}`}
        onClick={() => setExpanded((v) => !v)}
      >
        {/* Severity mark */}
        <span className={`mt-1 w-[3px] h-[15px] rounded-full shrink-0 ${markClass}`} />

        <div className="flex-1 min-w-0">
          {/* Top row: severity label + category badge + subcategory type */}
          <div className="flex flex-wrap items-center gap-2 mb-0.5">
            {/* The one pill style (DEC-193), in fixed slots so the pill and the
                category start at the same place on every row (decision 3). */}
            <span className="w-[92px] flex-none">
              {knownSeverity ? (
                <SeverityBadge severity={knownSeverity} />
              ) : (
                <TonePill tone="neutral">{finding.severity}</TonePill>
              )}
            </span>
            <span className="w-[150px] flex-none">
              <CategoryBadge category={finding.category} />
            </span>
            <span className="text-[12px] text-muted">{subLabel}</span>
            {/* DEC-067: provenance — how this finding was produced */}
            <span
              className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${
                isAiValidated(finding)
                  ? 'text-accent-text bg-accent/[0.14]'
                  : 'text-muted bg-fill-subtle'
              }`}
              title={provenanceTitle(finding)}
            >
              {isAiValidated(finding) ? 'AI-validated' : 'static'}
            </span>
            {isGrouped && (
              <span className="text-[11px] text-faint bg-fill-subtle px-1.5 py-0.5 rounded-full">
                {finding.occurrence_count}×
              </span>
            )}
          </div>

          {/* Summary text */}
          <p className="text-[13px] text-ink leading-snug">
            {finding.human_summary.slice(0, 100)}
            {finding.human_summary.length > 100 ? '…' : ''}
          </p>

          {/* File + effort */}
          {!isGrouped && (
            <p className="mt-1 flex items-center gap-2 flex-wrap">
              {hideFile ? (
                <FileChip
                  file={finding.file}
                  lineStart={finding.line_start}
                  lineEnd={finding.line_end}
                  repoUrl={repoUrl}
                  branch={branch}
                  lineOnly
                />
              ) : (
                <FileChip
                  file={finding.file}
                  lineStart={finding.line_start}
                  lineEnd={finding.line_end}
                  repoUrl={repoUrl}
                  branch={branch}
                />
              )}
              <span className="text-[11px] text-faint">
                {finding.estimated_effort_hours}h to fix
              </span>
            </p>
          )}
          {isGrouped && (
            <p className="text-[11px] text-faint mt-0.5">
              {finding.occurrence_count} occurrences · {finding.estimated_effort_hours}h to fix
            </p>
          )}
        </div>

        {/* Cost + chevron */}
        <div className="shrink-0 flex items-center gap-2 ml-2 mt-0.5">
          <span className="text-[13px] font-semibold text-body">
            ${cost.toLocaleString()}
          </span>
          {expanded
            ? <ChevronUp className="w-4 h-4 text-faint" />
            : <ChevronDown className="w-4 h-4 text-faint" />
          }
        </div>
      </button>

      {/* ── Expanded detail ── */}
      {expanded && (
        <div className="px-4 pb-4 pt-0 border-t border-hairline space-y-3 text-[13px]">
          {/* Meta */}
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-muted text-[12px] pt-3">
            {!isGrouped && (
              <FileChip
                file={finding.file}
                lineStart={finding.line_start}
                lineEnd={finding.line_end}
                repoUrl={repoUrl}
                branch={branch}
              />
            )}
            <span className="inline-flex items-center">
              Confidence: {Math.round(finding.confidence * 100)}%
              {finding.confidence_adjustment !== null &&
               finding.confidence_adjustment !== undefined &&
               finding.confidence_adjustment_reason && (
                <ConfidenceAdjBadge
                  adjustment={finding.confidence_adjustment}
                  reason={finding.confidence_adjustment_reason}
                />
              )}
            </span>
            <span>Effort: {finding.estimated_effort_hours}h</span>
            <span>Cost: ${cost.toLocaleString()}</span>
          </div>

          {/* What it is */}
          <div>
            <p className="text-[11px] font-semibold text-faint uppercase tracking-wider mb-1">
              What it is
            </p>
            <p className="text-body leading-relaxed">{finding.human_summary}</p>
          </div>

          {/* Why it costs */}
          {finding.why_it_costs && (
            <div>
              <p className="text-[11px] font-semibold text-faint uppercase tracking-wider mb-1">
                Why it costs your team
              </p>
              <p className="text-body leading-relaxed">{finding.why_it_costs}</p>
            </div>
          )}

          {/* Suggested direction */}
          {finding.suggested_direction && (
            <div>
              <p className="text-[11px] font-semibold text-faint uppercase tracking-wider mb-1">
                Suggested direction
              </p>
              <p className="text-body leading-relaxed whitespace-pre-line">
                {finding.suggested_direction}
              </p>
            </div>
          )}

          {/* Code snippet */}
          {finding.code_snippet && (
            <CodeView
              snippet={finding.code_snippet}
              highlightStart={finding.line_start}
              highlightEnd={finding.line_end}
              ext={fileExt}
              severity={finding.severity}
            />
          )}

          {/* Export stubs */}
          <div className="flex gap-3 pt-1">
            <button className="text-[12px] text-faint cursor-not-allowed" title="Coming soon">
              Export to Jira
            </button>
            <button className="text-[12px] text-faint cursor-not-allowed" title="Coming soon">
              Export to Linear
            </button>
          </div>

          {/* Feedback row — only shown when scanId is provided */}
          {scanId && (
            <FeedbackRow
              scanId={scanId}
              finding={finding}
              localVerdict={localVerdict}
              onVerdictChange={handleVerdictChange}
            />
          )}
        </div>
      )}
    </div>
  )
}
